import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { storage } from '@uhg-abyss/web/tools/storage';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import mapboxgl from 'mapbox-gl';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { useConfig } from '../../frontends/ProviderSearch/context/Analytics';
import { Popup } from '../../frontends/ProviderSearch/routes/MapView/Popup';
import { getEnableMapPinRedesign } from '../../frontends/ProviderSearch/routes/MapView/utils';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useUserLocation } from '../../hooks/useUserLocation';
import { FacilityDetails } from '../../models/FacilityDetails';
import { ProviderDetails } from '../../models/ProviderDetails';
import { Directions } from '../../models/RouteDirections';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { handleAWSMapClickLocation } from '../../utils/amazon.directions.utils';
import {
  getAmazonRoute,
  mapAWSViewUpdatePin,
} from '../../utils/amazon.map.utils';
import { handleMapClickLocation } from '../../utils/directions.utils';
import {
  getRoute,
  mapViewUpdatePin,
  setResultIcon,
  setZoomLevel,
  updateIconSize,
} from '../../utils/map.utils';
import {
  getProviderLocationWithProviderGroup,
  getSelectedLocationIndex,
} from '../../utils/providerDetails.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { AmazonMapDisplay } from '../AmazonMapDisplay';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { hideScrollbar, mediumScreenOnly } from '../ConstantsStyles';
import { MapDisplay } from '../MapDisplay';
import { MapViewSelectLocationCard } from '../MapViewSelectLocationCard';
import { getGeoLocationFromStorage } from '../PSXHeader/SearchBar/utils';
import { handleLinkAndModalTrack } from '../Utils/adobeTrackUtils';
import {
  convertCoverageType,
  convertProviderTypeToAdobeType,
} from '../Utils/adobeTrackUtils/adobeTrackUtils';
import {
  ChooseALocationHeadingWrap,
  ChooseLocationCardsContainer,
  ChooseLocationLabel,
  LocationsWrapColumn,
  MapViewWrap,
  MobileMapViewWrap,
  SidePaneCloseButton,
  ViewMapButton,
} from './MapViewLocationsWrap.style';
import { MobileViewHeadingNavigation } from './MobileViewHeadingNavigation';

type Props = {
  hasMultipleLocations: boolean;
  isMapView?: boolean;
  mobileScreen: boolean;
  providerDetails: ProviderDetails | FacilityDetails;
  updateLocationButton: React.ReactNode | React.ReactFragment;
  handleGoBack: (a: boolean) => void;
  setIsMapView: (isMapView: boolean) => void;
  useStickyButtonRow: boolean;
  setCurrentIndex: (currentIndex: number) => void;
  directionsButtonClicked: boolean;
  tabTitle?: string;
  mobileRouteView?: boolean;
  setMobileRouteView: (a: boolean) => void;
  skipNavigationFocus?: boolean;
};
export const MapViewLocationsWrap = ({
  hasMultipleLocations,
  isMapView = false,
  mobileScreen,
  providerDetails,
  updateLocationButton,
  handleGoBack,
  setIsMapView,
  setCurrentIndex,
  useStickyButtonRow,
  directionsButtonClicked,
  tabTitle,
  mobileRouteView = false,
  setMobileRouteView,
  skipNavigationFocus,
}: Props) => {
  const [viewMapEnhancementsFlag, enableMapPinRedesignFlag, switchToAWSMap] =
    useFeatureFlag([
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_PIN_REDESIGN,
      ConstantsLagoon.FEATURE_FLAGS.SWITCH_TO_AWS_MAPS,
    ]);
  const [showMapCollapse] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.SHOW_MAP_COLLAPSE,
  ]);
  const backBtnRef = React.useRef<HTMLAnchorElement>(null);
  const [closeSidePanel, setCloseSidePanel] = useState<boolean>(false);
  const [routeEndCoords, setRouteEndCoords] = useState<
    [number | null, number | null]
  >([null, null]);
  const [directions, setDirections] = useState<Directions>({
    userPlaceName: '',
    endPlaceName: '',
    routes: [],
  });

  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );
  const mediumScreen = useMediaQuery(mediumScreenOnly);
  const map = React.useRef<mapboxgl.Map | null>(null);
  const { t } = useTranslation();
  const { longitude, latitude } = getGeoLocationFromStorage();
  const [highlightId, setHighlightId] = useSessionStorage<any>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_HIGHLIGHT_ID,
    { providerId: '', from: '' }
  );
  const [selectedId, setSelectedId] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    ''
  );
  const providerLocations = useMemo(
    () =>
      getProviderLocationWithProviderGroup(providerDetails?.providerLocations),
    [providerDetails?.providerLocations]
  );

  const [mapPinCoords] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_COORDS,
    []
  );

  const selectedIdIndex = selectedId
    ? providerLocations?.findIndex(
        (location) => selectedId === location.locationId
      )
    : 0;

  const highlightRefs = React.useRef<any>({});
  const mobileMapViewClassName = mobileScreen ? 'location-card-map-view' : '';
  const tierLocationIndex = providerDetails?.providerLocations?.findIndex(
    (location) => location?.isTieredProvider === true
  );

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: `${tabTitle}:${Constants.PROVIDER_DETAILS.VIEWONMAP}`,
    sitesectionLevel1: Constants.ADOBE_TRACKING.DETAILS_SITESECTION1,
    sitesectionLevel2: `${convertProviderTypeToAdobeType(
      providerDetails.providerType
    )} details`,
    impressionBlock: {
      type: convertProviderTypeToAdobeType(providerDetails.providerType),
      indicator: '',
    },
    providerBlock: {
      type: convertCoverageType(coverageType),
      tier: tierLocationIndex !== -1 ? 'tier 1' : '',
    },
  });

  useEffect(() => {
    if (isMapView) adobePageTrackEvent();
  }, []);

  useEffect(() => {
    highlightRefs.current[highlightId.providerId]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }, [highlightId]);

  useEffect(() => {
    // Back button focus on the first render
    if (backBtnRef.current) backBtnRef.current.focus();
  }, []);

  useEffect(() => {
    map.current?.resize();
    if (!closeSidePanel) {
      setZoomLevel(map.current, mapPinCoords);
    }
  }, [closeSidePanel]);

  useEffect(() => {
    if (mobileScreen) setMobileRouteView(!!routeEndCoords[0]);
  }, [routeEndCoords, mobileScreen]);

  const handleMapButton = () => {
    setIsMapView(true);
  };

  const selectLocation = (index, location) => {
    const { locationId } = location;
    setSelectedId(locationId);
    setCurrentIndex(index);
    const linkName: string = `select location`;
    const linkLocation: string = `body:${Constants.PROVIDER_DETAILS.VIEWONMAP}`;
    const modalName: string = Constants.MAP_VIEW_NAVIGATION_MODAL;
    handleLinkAndModalTrack(linkName, linkLocation, modalName);
  };

  const mapOnPremKey = useConfig('MAPBOX_ONPREM_KEY');
  const mapOnPremURL = useConfig('MAPBOX_ONPREM_URL');
  const mapKey = useConfig('AWS_MAP_KEY');

  const navigationContainerRef = useRef<HTMLElement | null>(null);

  const [, getUserLocation] = useUserLocation({});

  const handleDirectionClick = async (location) => {
    const { longitude: locationLng, latitude: locationLat } = location;
    let routeDirections;
    if (switchToAWSMap) {
      routeDirections = await getAmazonRoute(
        map.current,
        longitude,
        latitude,
        +locationLng,
        +locationLat,
        'driving-traffic',
        0,
        mapKey
      );
    } else {
      routeDirections = await getRoute(
        map.current,
        longitude,
        latitude,
        +locationLng,
        +locationLat,
        getUserLocation,
        mediumScreen,
        'driving-traffic',
        0,
        mapOnPremKey,
        mapOnPremURL
      );
    }
    if (routeDirections) {
      setDirections(routeDirections);
      setTimeout(() => {
        if (navigationContainerRef.current)
          navigationContainerRef.current?.focus();
      }, 1000);
    }
    setRouteEndCoords([locationLng, locationLat]);
    if (mobileScreen) setMobileRouteView(true);
  };

  const handleLocationCardMouseEnter = async (
    index,
    location,
    flyToPin = false
  ) => {
    if (mobileScreen) return;
    await setZoomLevel(map?.current, mapPinCoords);

    if (viewMapEnhancementsFlag) {
      const marker = document.querySelector(
        `#custom-marker-${location.locationId}`
      );
      updateIconSize(true, marker, true);
    }
    setResultIcon(
      map.current,
      index,
      true,
      setHighlightId,
      location.locationId
    );

    if (flyToPin) {
      map.current?.flyTo({
        center: [+location.longitude, +location.latitude],
        zoom: Constants.DEVICE_LOCATION.ZOOM,
      });
    }
    const pinData = {
      providerId: location.locationId,
      address: getProviderLocation(location.locationId, providerLocations)
        ?.address,
      providerName: getProviderName(location.locationId, providerLocations),
      latitude: location.latitude,
      longitude: location.longitude,
    };
    switchToAWSMap
      ? mapAWSViewUpdatePin(
          pinData,
          flyToPin,
          popupContent,
          setPopupContent,
          map
        )
      : mapViewUpdatePin(pinData, flyToPin, popupContent, setPopupContent, map);
  };

  const handleLocationCardMouseLeave = (index, location) => {
    if (viewMapEnhancementsFlag) {
      const marker = document.querySelector(
        `#custom-marker-${location.locationId}`
      );
      updateIconSize(false, marker, true);
    }
    if (!selectedId) {
      setResultIcon(map.current, index, false, setHighlightId, null);
    }
  };

  const displayRoute = async (locationLng, locationLat) => {
    let routeDirections;
    if (switchToAWSMap) {
      routeDirections = await getAmazonRoute(
        map.current,
        longitude,
        latitude,
        +locationLng,
        +locationLat,
        'driving-traffic',
        0,
        mapKey
      );
    } else {
      routeDirections = await getRoute(
        map.current,
        longitude,
        latitude,
        +locationLng,
        +locationLat,
        getUserLocation,
        false,
        'driving-traffic',
        0,
        mapOnPremKey,
        mapOnPremURL
      );
    }
    if (routeDirections) setDirections(routeDirections);
  };

  useEffect(() => {
    if (directionsButtonClicked && map.current) {
      const {
        locationId,
        longitude: locationLng,
        latitude: locationLat,
      } = providerLocations[selectedIdIndex];
      setRouteEndCoords([Number(locationLng), Number(locationLat)]);
      setSelectedId(locationId);
      map.current?.on('load', () => {
        displayRoute(locationLng, locationLat);
      });
    }
  }, [map.current]);

  const chooseALocationHeading =
    hasMultipleLocations && !isMapView && !mobileScreen ? (
      <ChooseALocationHeadingWrap
        data-auto-testid="top-locations-label-and-back-navigation"
        data-testid="top-locations-label-and-back-navigation"
      >
        <ChooseLocationLabel color="$core.color.brand.100" fontWeight="bold">
          {t('PROVIDER_DETAILS.LOCATIONS')}
          {` (${providerLocations.length})`}
        </ChooseLocationLabel>
        <ViewMapButton
          data-auto-testid="map-button"
          data-testid="map-button"
          onClick={handleMapButton}
          variant="text"
        >
          <IconSymbol
            color="$core.color.brand.60"
            icon="map"
            isScreenReadable
            size="15px"
          />
          {t('View Map')}
        </ViewMapButton>
      </ChooseALocationHeadingWrap>
    ) : null;

  const wrapperSelectedStyle = {
    border: '3px solid #002677',
    borderRadius: '12px',
  };

  const handleClickLocation = async (locationId, locationLng, locationLat) => {
    if (switchToAWSMap) {
      await handleAWSMapClickLocation(
        map.current,
        selectedId,
        longitude,
        latitude,
        locationId,
        locationLng,
        locationLat,
        mapKey,
        mobileScreen,
        setDirections,
        setRouteEndCoords,
        setSelectedId
      );
    } else {
      await handleMapClickLocation(
        map.current,
        selectedId,
        longitude,
        latitude,
        locationId,
        locationLng,
        locationLat,
        mapOnPremKey,
        mapOnPremURL,
        mobileScreen,
        setDirections,
        setRouteEndCoords,
        setSelectedId,
        getUserLocation
      );
    }
  };

  const getProviderLocation = (providerId, locations) =>
    locations.find((location) => location.locationId === providerId);

  const getProviderName = (providerId, locations) => {
    const providerLocation = locations.find(
      (location) => location.locationId === providerId
    );
    if (providerLocation) {
      return providerLocation.providerGroupsAffiliations?.[0]?.value;
    }
    return '';
  };

  const [popupContent, setPopupContent] = useState<any>('');

  const forceMapPinRedesign = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN
  );

  const enableMapPinRedesign: boolean = getEnableMapPinRedesign(
    enableMapPinRedesignFlag,
    forceMapPinRedesign
  );

  const updatePin = (practitioner, flyToPin = false) => {
    if (enableMapPinRedesign) {
      if (!practitioner) {
        switchToAWSMap
          ? mapAWSViewUpdatePin(null, false, popupContent, setPopupContent, map)
          : mapViewUpdatePin(null, false, popupContent, setPopupContent, map);
        return;
      }

      return;
    }

    if (mobileScreen) {
      setPopupContent({ ...practitioner });
      return;
    }
    switchToAWSMap
      ? mapAWSViewUpdatePin(
          practitioner,
          flyToPin,
          popupContent,
          setPopupContent,
          map
        )
      : mapViewUpdatePin(
          practitioner,
          flyToPin,
          popupContent,
          setPopupContent,
          map
        );
  };

  return (
    <>
      <MobileMapViewWrap
        className={mobileScreen ? 'show-mobile-map-view' : ''}
        data-testid="map-view-wrap-locations"
        style={{ paddingRight: viewMapEnhancementsFlag ? '0' : '' }}
      >
        <LocationsWrapColumn
          className="locations-wrap-column"
          css={hideScrollbar}
        >
          {!closeSidePanel && (
            <div>
              {!mobileScreen ? (
                <Link
                  beforeIcon="arrow_back"
                  data-auto-testid="back-to-locations-details"
                  data-testid="back-to-locations-details"
                  isStandardAnchor
                  onClick={handleGoBack}
                  ref={backBtnRef}
                >
                  <Text color="$core.color.brand.60" fontWeight="bold">
                    {t('BACK')}
                  </Text>
                </Link>
              ) : null}
              {chooseALocationHeading}
              <ChooseLocationCardsContainer
                alignItems="flex-end"
                aria-label={t('ACCESSIBILITY_LABELS.LOCATIONS')}
                className="choose-location-container"
                role="group"
              >
                {!mobileRouteView
                  ? providerLocations?.map((location, index) => (
                      <div
                        aria-pressed={selectedId === location.locationId}
                        className="location-cards-container"
                        data-auto-testid="location-section"
                        data-testid="location-section"
                        key={location.locationId}
                        onBlur={() =>
                          handleLocationCardMouseLeave(index, location)
                        }
                        onFocus={() =>
                          handleLocationCardMouseEnter(index, location)
                        }
                        onMouseEnter={() =>
                          handleLocationCardMouseEnter(index, location)
                        }
                        onMouseLeave={() =>
                          handleLocationCardMouseLeave(index, location)
                        }
                        style={
                          selectedId === location.locationId
                            ? wrapperSelectedStyle
                            : {}
                        }
                      >
                        <MapViewSelectLocationCard
                          className={`
                      ${mobileMapViewClassName} ${
                            highlightId.providerId === location.locationId
                              ? 'highlight'
                              : ''
                          }
                    `}
                          handleDirectionClick={() =>
                            handleDirectionClick(location)
                          }
                          highlightRefs={highlightRefs}
                          index={index}
                          location={location}
                          selectLocation={() => selectLocation(index, location)}
                          selected={selectedId === location.locationId}
                          showBadgeRow
                          totalLocations={providerLocations.length}
                        />
                      </div>
                    ))
                  : null}
              </ChooseLocationCardsContainer>
              {useStickyButtonRow ? updateLocationButton : null}
            </div>
          )}
          {showMapCollapse ? (
            <SidePaneCloseButton
              alignItems="center"
              className="side-btn"
              justify="center"
            >
              <IconSymbol
                css={{
                  fill: 'black',
                }}
                data-auto-testid="sidepanel-close-icon"
                data-testid="sidepanel-close-icon"
                icon={
                  closeSidePanel
                    ? 'keyboard_arrow_right'
                    : 'keyboard_arrow_left'
                }
                onClick={() => {
                  setCloseSidePanel(!closeSidePanel);
                  adobeLinkTrackEvent({
                    name: `sidepanel:${closeSidePanel ? 'open' : 'close'}`,
                    location: 'body:map',
                    type: 'internal',
                  });
                }}
                size="$md"
              />
            </SidePaneCloseButton>
          ) : null}
        </LocationsWrapColumn>
        {!mobileRouteView ? (
          <MobileViewHeadingNavigation
            handleGoBack={handleGoBack}
            mobileScreen={mobileScreen}
            providerDetails={providerDetails}
          />
        ) : null}
        <MapViewWrap
          cssProps={{
            viewMapEnhancementsFlag,
            closeSidePanel,
            mobileRouteView,
          }}
        >
          {providerLocations?.length ? (
            switchToAWSMap ? (
              <AmazonMapDisplay
                coords={{ longitude, latitude }}
                directions={directions}
                fullWidth={viewMapEnhancementsFlag}
                isLocationsTab
                locationResults={providerLocations}
                map={map}
                mobileRouteView={mobileRouteView}
                mobileScreen={mobileScreen}
                navigationRef={navigationContainerRef}
                refToBeFocusedOnDirectionsClose={
                  highlightRefs.current[highlightId.providerId] as HTMLElement
                }
                routeEndCoords={routeEndCoords}
                selectLocation={handleClickLocation}
                setPopupContent={setPopupContent}
                setRouteEndCoords={setRouteEndCoords}
                skipNavigationFocus={skipNavigationFocus}
                updatePin={updatePin}
              />
            ) : (
              <MapDisplay
                coords={{ longitude, latitude }}
                directions={directions}
                fullWidth={viewMapEnhancementsFlag}
                isLocationsTab
                locationResults={providerLocations}
                map={map}
                mobileRouteView={mobileRouteView}
                mobileScreen={mobileScreen}
                navigationRef={navigationContainerRef}
                refToBeFocusedOnDirectionsClose={
                  highlightRefs.current[highlightId.providerId] as HTMLElement
                }
                routeEndCoords={routeEndCoords}
                selectLocation={handleClickLocation}
                setPopupContent={setPopupContent}
                setRouteEndCoords={setRouteEndCoords}
                skipNavigationFocus={skipNavigationFocus}
                updatePin={updatePin}
              />
            )
          ) : null}
        </MapViewWrap>
      </MobileMapViewWrap>
      {!mobileScreen && (
        <React.Fragment>
          <Popup
            handleClickLocation={handleClickLocation}
            index={getSelectedLocationIndex(
              providerLocations,
              highlightId.providerId
            )}
            isChooseLocation={true}
            practitioner={{
              providerId: highlightId.providerId,
              address: getProviderLocation(
                highlightId.providerId,
                providerLocations
              )?.address,
              providerName: getProviderName(
                highlightId.providerId,
                providerLocations
              ),
            }}
            selectLocation={() =>
              selectLocation(
                getSelectedLocationIndex(
                  providerLocations,
                  highlightId.providerId
                ),
                getProviderLocation(highlightId.providerId, providerLocations)
              )
            }
            selected={selectedId === highlightId.providerId}
            totalLocations={providerLocations.length}
            updatePin={updatePin}
          />
        </React.Fragment>
      )}
    </>
  );
};
